// Which products the account has, from GET /users/:uid/products (Trello #410). Gating is UI-only
// and fail-open: if the request fails, nothing is locked rather than locking the user out of the
// whole workbench.
import { useCallback, useEffect, useState } from 'react';
import { catalogEntry, hasEntitlementKey, isEntitled } from '../config/catalog';
import type { CatalogId } from '../config/catalog';
import { DATA_PRODUCT_ENTITLEMENT_KEYS } from '../config/products';
import type { DataProductId } from '../config/products';
import { loadEntitlements, peekEntitlements } from '../services/entitlementsCache';
import type { ProductEntitlement } from '../services/productsApi';

export function useProductEntitlements(): {
  loading: boolean;
  isLocked(productKey: string): boolean;
  isDataProductLocked(p: DataProductId): boolean;
  /** Is this catalog entry (product, service or open data) out of the plan? Uses the service fallback. */
  isEntryLocked(id: CatalogId): boolean;
} {
  const [cached] = useState(peekEntitlements);
  const [products, setProducts] = useState<Record<string, ProductEntitlement>>(cached ?? {});
  // Already cached: no spinner.
  const [loading, setLoading] = useState(cached === undefined);
  // Fail-open: if entitlements can't be resolved, don't gate anything.
  const [gatingActive, setGatingActive] = useState(cached != null);

  useEffect(() => {
    if (cached !== undefined) return;
    let cancelled = false;
    loadEntitlements().then((res) => {
      if (cancelled) return;
      if (res) {
        setProducts(res);
        setGatingActive(true);
      } else {
        setGatingActive(false); // fail-open — show everything rather than block the workbench
      }
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [cached]);

  const isLocked = useCallback(
    (productKey: string): boolean => {
      if (!gatingActive) return false; // fail-open / still resolving
      return !hasEntitlementKey(productKey, products);
    },
    [gatingActive, products],
  );

  const isDataProductLocked = useCallback(
    (p: DataProductId): boolean => isLocked(DATA_PRODUCT_ENTITLEMENT_KEYS[p]),
    [isLocked],
  );

  const isEntryLocked = useCallback(
    (id: CatalogId): boolean => gatingActive && !isEntitled(catalogEntry(id), products),
    [gatingActive, products],
  );

  return { loading, isLocked, isDataProductLocked, isEntryLocked };
}
